import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Clock } from 'lucide-react';
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import comissaoReguaService from 'features/comercial/services/comissaoReguaService';
import useCompanyContextKey from 'hooks/useCompanyContextKey';

/**
 * Ritmo da equipe interna: a meta do dia ao longo do mês e as vendas de hoje hora a hora.
 *
 * Meta e supermeta são as PLANEJADAS da equipe, as mesmas que dão a linha da corrida no Ranking
 * (lá divididas pelas internas). Não seguem o filtro de período da página: é sempre o mês corrente.
 */

const caixa = 'rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)]';
const eixo = { fill: 'var(--text-muted)', fontSize: 11 };
const ATUALIZAR_A_CADA_MS = 5 * 60 * 1000;
/** A curva começa antes da primeira venda, para a subida ter de onde sair. */
const PRIMEIRA_HORA = 7;
/** Legenda em tinta de texto: a cor da série fica no ícone ao lado. */
const textoDaLegenda = (valor) => <span style={{ color: 'var(--text-muted)' }}>{valor}</span>;

const DIAS_DA_SEMANA = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** `dia_semana` vem como o `DAYOFWEEK` do MySQL: 1 = domingo … 7 = sábado. */
export const serieDoMes = (mes = []) => mes.map((dia) => {
  const semana = DIAS_DA_SEMANA[(Number(dia.dia_semana) || 1) - 1];
  return {
    ...dia,
    dia: String(dia.data || '').slice(8, 10),
    semana,
    // O eixo marca o feriado no lugar do dia da semana, como o painel do Grafana.
    marca: dia.feriado ? 'fer' : semana.slice(0, 3),
    fimDeSemana: !dia.feriado && (Number(dia.dia_semana) === 1 || Number(dia.dia_semana) === 7),
    supermeta: dia.supermeta > dia.meta ? dia.supermeta : null,
  };
});

/** Dia e, embaixo, dia da semana ou "fer". Dia útil em tinta normal; fim de semana e feriado apagados. */
const MarcaDoDia = ({ x, y, payload, dados }) => {
  const dia = dados[payload.index] || {};
  const apagado = dia.feriado || dia.fimDeSemana;
  return (
    <text x={x} y={y + 10} textAnchor="middle" fontSize={11} fill="var(--text-muted)" opacity={apagado ? 0.7 : 1}>
      <tspan x={x}>{dia.dia}</tspan>
      <tspan x={x} dy={13} fontSize={10} fontWeight={dia.feriado ? 600 : 400}>{dia.marca}</tspan>
    </text>
  );
};

/**
 * Pontos da curva de hoje: das 7h (ou antes, se já houve venda antes) até a hora atual ou a da
 * última venda. Hora que ainda não chegou não vira ponto — a linha pararia no plano e pareceria
 * que o dia acabou.
 */
export const curvaDeHoje = (horas = [], horaAtual = new Date().getHours()) => {
  const comVenda = horas.filter((h) => h.vendas > 0).map((h) => h.hora);
  if (horas.length === 0) return [];
  const inicio = Math.min(PRIMEIRA_HORA, ...comVenda);
  const fim = Math.max(horaAtual, ...comVenda);

  return horas
    .filter((h) => h.hora >= inicio && h.hora <= fim)
    .map((h) => ({ ...h, rotulo: `${String(h.hora).padStart(2, '0')}h` }));
};

const LinhaDoTooltip = ({ rotulo, valor }) => (
  <p className="text-[color:var(--text-muted)]">{rotulo}: <strong className="tabular-nums text-[color:var(--text-primary)]">{valor}</strong></p>
);

const TooltipDoMes = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const dia = payload[0].payload;
  return (
    <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-[color:var(--text-primary)]">Dia {dia.dia} · {dia.semana}{dia.feriado ? ' · feriado' : ''}</p>
      <LinhaDoTooltip rotulo="Vendido" valor={dia.vendas === null ? '—' : dia.vendas} />
      <LinhaDoTooltip rotulo="Meta do dia" valor={dia.meta} />
      {dia.supermeta !== null && <LinhaDoTooltip rotulo="Supermeta do dia" valor={dia.supermeta} />}
    </div>
  );
};

const TooltipDeHoje = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const ponto = payload[0].payload;
  return (
    <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface-strong)] px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-[color:var(--text-primary)]">Até as {String(ponto.hora).padStart(2, '0')}h59</p>
      <LinhaDoTooltip rotulo="Acumulado" valor={ponto.acumulado} />
      <LinhaDoTooltip rotulo="Nesta hora" valor={ponto.vendas} />
    </div>
  );
};

const Cabecalho = ({ titulo, nota, children }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[color:var(--border)] px-5 py-4">
    <div>
      <h3 className="font-semibold text-[color:var(--text-primary)]">{titulo}</h3>
      <p className="mt-1 text-sm text-[color:var(--text-muted)]">{nota}</p>
    </div>
    {children}
  </div>
);

const Aviso = ({ children }) => (
  <p className="px-5 py-10 text-center text-sm text-[color:var(--text-muted)]">{children}</p>
);

const MesDaEquipe = ({ mes }) => {
  const dados = serieDoMes(mes);
  const temSupermeta = dados.some((d) => d.supermeta !== null);
  const batidos = dados.filter((d) => d.vendas !== null && d.meta > 0 && d.vendas >= d.meta).length;
  const passados = dados.filter((d) => d.vendas !== null && d.meta > 0).length;

  return (
    <section className={`${caixa} overflow-hidden lg:col-span-3`} aria-label="Meta do dia ao longo do mês">
      <Cabecalho
        titulo="Meta do dia ao longo do mês"
        nota="Vendas das internas contra a meta e a supermeta planejadas para cada dia. Cada dia pesa o que costuma vender: domingo, sábado e feriado (cadastro de feriados do IXC) têm meta menor. Inclui pré-contratos, como o Ranking."
      >
        {passados > 0 && (
          <p className="text-sm text-[color:var(--text-muted)]">
            Meta batida em <strong className="tabular-nums text-[color:var(--text-primary)]">{batidos}</strong> de {passados} dias
          </p>
        )}
      </Cabecalho>
      <div className="overflow-x-auto px-2 pb-3 pt-4 sm:px-4">
        <div className="h-72 min-w-[640px]" aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={dados} margin={{ top: 8, right: 8, left: -18, bottom: 12 }}>
              <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis dataKey="dia" tickLine={false} axisLine={false} tick={<MarcaDoDia dados={dados} />} interval={0} height={36} />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={eixo} />
              <Tooltip content={<TooltipDoMes />} cursor={{ fill: 'var(--surface-muted)' }} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} formatter={textoDaLegenda} />
              <Bar dataKey="vendas" name="Vendido" fill="var(--accent)" radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
              <Line dataKey="meta" name="Meta do dia" type="linear" stroke="var(--text-primary)" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 2.5, strokeDasharray: '' }} isAnimationActive={false} />
              {temSupermeta && (
                <Line dataKey="supermeta" name="Supermeta do dia" type="linear" stroke="var(--text-muted)" strokeWidth={2} strokeDasharray="2 3" dot={{ r: 2.5, strokeDasharray: '' }} isAnimationActive={false} />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <table className="sr-only">
        <caption>Vendas das internas, meta e supermeta por dia do mês</caption>
        <thead><tr><th scope="col">Data</th><th scope="col">Dia</th><th scope="col">Vendido</th><th scope="col">Meta do dia</th><th scope="col">Supermeta do dia</th></tr></thead>
        <tbody>
          {dados.map((dia) => (
            <tr key={dia.data}>
              <th scope="row">{dia.data}</th>
              <td>{dia.semana}{dia.feriado ? ', feriado' : ''}</td>
              <td>{dia.vendas === null ? 'Ainda não ocorreu' : dia.vendas}</td>
              <td>{dia.meta}</td>
              <td>{dia.supermeta ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

/** O selo do dia: ícone e texto, nunca só a cor. */
const SeloDeHoje = ({ vendas, meta, supermeta }) => {
  if (meta <= 0) return null;
  const bateuSuper = supermeta > meta && vendas >= supermeta;
  const bateu = vendas >= meta;
  if (!bateu) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2.5 py-1 text-xs font-medium text-[color:var(--text-primary)]">
        <Clock className="h-3.5 w-3.5" aria-hidden="true" />Faltam {meta - vendas} para a meta
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--status-good-border)] bg-[color:var(--status-good-bg)] px-2.5 py-1 text-xs font-medium text-[color:var(--status-good-fg)]">
      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />{bateuSuper ? 'Supermeta do dia batida' : 'Meta do dia batida'}
    </span>
  );
};

const HojeDaEquipe = ({ hoje }) => {
  const pontos = curvaDeHoje(hoje.horas);
  // Quatro faixas iguais com folga acima da supermeta: a linha dela nunca encosta no teto.
  const passo = Math.max(5, Math.ceil((Math.max(hoje.supermeta, hoje.meta, hoje.vendas, 1) * 1.1) / 4 / 5) * 5);
  const marcas = [0, passo, passo * 2, passo * 3, passo * 4];
  const rotuloDaLinha = (texto) => ({ value: texto, position: 'insideTopLeft', fill: 'var(--text-muted)', fontSize: 11 });

  return (
    <section className={`${caixa} overflow-hidden lg:col-span-2`} aria-label="Vendas de hoje hora a hora">
      <Cabecalho
        titulo="Hoje, hora a hora"
        nota={<><strong className="tabular-nums text-[color:var(--text-primary)]">{hoje.vendas}</strong> vendas das internas · meta {hoje.meta}{hoje.supermeta > hoje.meta ? ` · supermeta ${hoje.supermeta}` : ''}</>}
      >
        <SeloDeHoje vendas={hoje.vendas} meta={hoje.meta} supermeta={hoje.supermeta} />
      </Cabecalho>
      {hoje.horas_indisponiveis ? (
        <Aviso>O IXC não respondeu à leitura das horas. O total do dia acima continua valendo.</Aviso>
      ) : (
        <>
          <div className="px-2 pb-1 pt-4 sm:px-4">
            <div className="h-64" aria-hidden="true">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={pontos} margin={{ top: 16, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="var(--border)" strokeDasharray="3 3" />
                  <XAxis dataKey="rotulo" tickLine={false} axisLine={false} tick={eixo} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={eixo} domain={[0, marcas[4]]} ticks={marcas} />
                  <Tooltip content={<TooltipDeHoje />} cursor={{ stroke: 'var(--border)' }} />
                  {hoje.meta > 0 && <ReferenceLine y={hoje.meta} stroke="var(--text-primary)" strokeWidth={2} strokeDasharray="6 4" label={rotuloDaLinha(`Meta ${hoje.meta}`)} />}
                  {hoje.supermeta > hoje.meta && <ReferenceLine y={hoje.supermeta} stroke="var(--text-muted)" strokeWidth={2} strokeDasharray="2 3" label={rotuloDaLinha(`Supermeta ${hoje.supermeta}`)} />}
                  <Line dataKey="acumulado" name="Acumulado" type="stepAfter" stroke="var(--accent)" strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <p className="px-5 pb-4 text-xs text-[color:var(--text-muted)]">
            A hora é a de quando o contrato entrou no IXC.
            {hoje.sem_hora > 0 && ` ${hoje.sem_hora} ${hoje.sem_hora === 1 ? 'venda não tem' : 'vendas não têm'} hora registrada e ${hoje.sem_hora === 1 ? 'conta' : 'contam'} só no total.`}
          </p>
          <table className="sr-only">
            <caption>Vendas das internas acumuladas por hora hoje</caption>
            <thead><tr><th scope="col">Hora</th><th scope="col">Nesta hora</th><th scope="col">Acumulado</th></tr></thead>
            <tbody>
              {pontos.map((p) => <tr key={p.hora}><th scope="row">{p.rotulo}</th><td>{p.vendas}</td><td>{p.acumulado}</td></tr>)}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
};

const RitmoDaEquipe = () => {
  const empresa = useCompanyContextKey();
  const [estado, setEstado] = useState({ carregando: true, erro: false, dados: null });

  const carregar = useCallback(() => comissaoReguaService.ritmoDaEquipe()
    .then((dados) => setEstado({ carregando: false, erro: false, dados }))
    .catch(() => setEstado((anterior) => ({ ...anterior, carregando: false, erro: true }))), []);

  useEffect(() => {
    setEstado({ carregando: true, erro: false, dados: null });
    carregar();
    const intervalo = setInterval(carregar, ATUALIZAR_A_CADA_MS);
    return () => clearInterval(intervalo);
  }, [carregar, empresa]);

  const { carregando, erro, dados } = estado;
  if (carregando) {
    return <div className={`${caixa} mb-8 h-80 animate-pulse`} aria-label="Carregando o ritmo da equipe" />;
  }
  if (erro && !dados) {
    return <div className={`${caixa} mb-8`}><Aviso>Não foi possível carregar o ritmo da equipe. A tela tenta de novo em alguns minutos.</Aviso></div>;
  }
  if (dados.indisponivel || dados.internas === 0) {
    return (
      <div className={`${caixa} mb-8`}>
        <Aviso>{dados.indisponivel ? 'O IXC não respondeu à leitura da meta do dia.' : 'Nenhuma interna tem meta neste mês: não há meta do dia para acompanhar.'}</Aviso>
      </div>
    );
  }

  return (
    <div className="mb-8 grid gap-6 lg:grid-cols-5">
      <MesDaEquipe mes={dados.mes} />
      <HojeDaEquipe hoje={dados.hoje} />
    </div>
  );
};

export default RitmoDaEquipe;
